import { useId, useCallback, forwardRef } from "react";
import { VisuallyHidden } from "react-aria";
import { CoreSlider, type PropsWithOptionalTestID } from "@boi/components-core";
import { MinusIcon, PlusIcon } from "@boi/react-icons";

import * as S from "./Slider.styled";
import { SliderBase, type SliderBaseProps } from "../SliderBase";
import { SliderTooltip } from "./SliderTooltip";
import { IconButton, type IconButtonProps } from "../IconButton";

export type SliderProps = PropsWithOptionalTestID &
  Omit<SliderBaseProps, "defaultValue" | "onChange"> & {
    onChange: (value: number) => void;
    decrementButtonProps?: Omit<
      IconButtonProps,
      "variant" | "onClick" | "aria-describedby" | "children"
    >;
    incrementButtonProps?: Omit<
      IconButtonProps,
      "variant" | "onClick" | "aria-describedby" | "children"
    >;
  };

const Slider = forwardRef<HTMLDivElement, SliderProps>((props, ref) => {
  const {
    id,
    onChange,
    decrementButtonProps,
    incrementButtonProps,
    testID = "slider",
    ...otherProps
  } = props;

  const {
    step = CoreSlider.DEFAULT_STEP,
    value,
    minValue = CoreSlider.DEFAULT_MIN_VALUE,
    maxValue = CoreSlider.DEFAULT_MAX_VALUE,
  } = props;

  const fallbackID = useId();

  function handleDecrementClick() {
    if (value <= minValue) {
      return;
    }

    let newValue = value - step;

    if (newValue < minValue) {
      newValue = minValue;
    }

    onChange(newValue);
  }

  function handleIncrementClick() {
    const newValue = value + step;

    if (newValue > maxValue) {
      return;
    }

    onChange(newValue);
  }

  const handleChange = useCallback(
    (newValue: number | number[]): void => {
      if (newValue instanceof Array) {
        newValue[0] && onChange(newValue[0]);
      } else {
        onChange(newValue);
      }
    },
    [onChange]
  );

  return (
    <div ref={ref} data-testid={testID}>
      <SliderBase {...otherProps} id={id || fallbackID} onChange={handleChange}>
        <S.Container>
          <div>
            <IconButton
              aria-label="Decrease value"
              aria-describedby={id || fallbackID}
              testID={`${testID}-decrement-button`}
              {...decrementButtonProps}
              variant="contained"
              onClick={handleDecrementClick}
            >
              <MinusIcon />
            </IconButton>
          </div>

          <SliderBase.UI {...otherProps} />

          <div>
            <IconButton
              aria-label="Increase value"
              aria-describedby={id || fallbackID}
              testID={`${testID}-increment-button`}
              {...incrementButtonProps}
              variant="contained"
              onClick={handleIncrementClick}
            >
              <PlusIcon />
            </IconButton>
          </div>
        </S.Container>
      </SliderBase>
      <VisuallyHidden aria-live="assertive">{value}</VisuallyHidden>
      <SliderTooltip {...props} />
    </div>
  );
});

Slider.displayName = "Slider";

export default Slider;
